import { Fragment, useEffect, useState } from 'react';
import { Cpu, RefreshCw, RotateCcw } from 'lucide-react';
import {
  discardJob,
  jobSummary,
  listJobs,
  retryAllDead,
  retryJob,
  type JobCount,
  type JobRow,
  type JobStatus,
} from '../../lib/jobs-api';
import { Button } from '../../components/ui';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';

const STATUSES: JobStatus[] = ['dead', 'pending', 'running', 'done'];

// Background job queue (super-admin). Defaults to the dead-letter view: jobs
// that exhausted their retries, with the last error and a replay button.
export function AdminJobs() {
  const [status, setStatus] = useState<JobStatus>('dead');
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [counts, setCounts] = useState<JobCount[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  async function load(s: JobStatus = status) {
    setLoading(true);
    setError(null);
    try {
      const [j, c] = await Promise.all([listJobs(s), jobSummary()]);
      setJobs(j);
      setCounts(c);
    } catch {
      setError('load-failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function act(id: string, fn: (id: string) => Promise<unknown>) {
    setBusy(id);
    try {
      await fn(id);
      await load();
    } catch {
      setError('action-failed');
    } finally {
      setBusy(null);
    }
  }

  async function replayAll() {
    if (!(await confirmDialog({ title: `Retry all ${total('dead')} dead jobs?`, message: 'Do this once whatever broke them is fixed.', tone: 'default', confirmLabel: 'Retry all' }))) return;
    setBusy('all');
    try {
      await retryAllDead();
      await load();
    } catch {
      setError('action-failed');
    } finally {
      setBusy(null);
    }
  }

  const total = (s: JobStatus) => counts.filter((c) => c.status === s).reduce((n, c) => n + c.count, 0);

  return (
    <div>
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <Cpu size={13} />
            <span>Platform Engine</span>
          </div>
          <h1 className={styles.title}>Background Jobs</h1>
          <p className={styles.subtitle}>
            Failed jobs retry automatically with increasing delays. Jobs that run out of retries land in dead; replay once resolved.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {status === 'dead' && jobs.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              disabled={busy === 'all'}
              onClick={() => void replayAll()}
              className="gap-2 h-9 px-3 text-xs"
            >
              <RotateCcw size={13} />
              <span>Retry All Dead</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={loading}
            onClick={() => void load()}
            className="gap-2 h-9 px-3 text-xs"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {error && <p className={styles.error} style={{ marginBottom: 16 }} role="alert">{error}</p>}

      {/* Status Filter Chips */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        {STATUSES.map((s) => {
          const count = total(s);
          const isSelected = status === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: isSelected ? 600 : 500,
                cursor: 'pointer',
                border: isSelected ? '1px solid var(--inic-accent)' : '1px solid var(--inic-card-border)',
                background: isSelected ? 'rgba(9, 121, 245, 0.12)' : 'var(--inic-card-bg)',
                color: isSelected ? 'var(--inic-accent)' : 'var(--inic-ink-muted)',
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{ textTransform: 'capitalize' }}>{s}</span>
              <span
                style={{
                  padding: '1px 6px',
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 600,
                  background: isSelected ? 'var(--inic-accent)' : 'var(--inic-paper-recessed)',
                  color: isSelected ? '#ffffff' : 'var(--inic-ink-subtle)',
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <section className={styles.section} style={{ padding: 0, border: 'none', background: 'transparent' }}>
        {loading ? (
          <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>Loading…</p>
        ) : jobs.length === 0 ? (
          <div className={styles.emptyState}>
            <Cpu size={32} className={styles.emptyIcon} />
            <div className={styles.emptyTitle}>No {status} jobs</div>
            <div className={styles.emptyDescription}>Queue is clear and healthy.</div>
          </div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Updated</th><th>Queue</th><th>Attempts</th><th>Last error</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <Fragment key={j.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === j.id ? null : j.id)}>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(j.updatedAt).toLocaleString()}</td>
                      <td><span className={styles.badgeMuted}>{j.queue}</span></td>
                      <td>{j.attempts}/{j.maxAttempts}</td>
                      <td className={styles.mono} style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.lastError ?? ''}>
                        {j.lastError ?? '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                        {j.status === 'dead' && (
                          <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs px-2.5"
                              disabled={busy === j.id}
                              onClick={() => void act(j.id, retryJob)}
                            >
                              Replay
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs px-2 text-destructive hover:bg-destructive/10"
                              disabled={busy === j.id}
                              onClick={async () => {
                                if (await confirmDialog({ title: 'Discard this job permanently?', confirmLabel: 'Discard' })) void act(j.id, discardJob);
                              }}
                            >
                              Discard
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {expanded === j.id && (
                      <tr>
                        <td colSpan={5} style={{ background: 'var(--inic-surface-2, rgba(0,0,0,0.02))' }}>
                          {j.lastError && <p style={{ margin: '0 0 var(--inic-space-2)', color: 'var(--inic-danger, #b91c1c)', fontSize: 'var(--inic-size-xs)' }}>error: {j.lastError}</p>}
                          <span className={styles.label}>Payload</span>
                          <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                            {JSON.stringify(j.payload, null, 2)}
                          </pre>
                          {j.idempotencyKey && <p className={styles.mono} style={{ fontSize: 'var(--inic-size-xs)', margin: 0 }}>key: {j.idempotencyKey}</p>}
                          <p className={styles.mono} style={{ fontSize: 'var(--inic-size-xs)', margin: 0 }}>
                            priority: {j.priority}{j.siteId && ` · site: ${j.siteId}`}
                          </p>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
